import { calculateImpressions } from '../../../../services/display';

/**
 * Redistribute pending shows (paid + optionally subsidized) across a set of relations,
 * preserving consumed shows attached to the relation that already emitted them.
 *
 * Used when a display gains/loses an active relation (new ad version, ad change,
 * tolerance change, etc.) and the spots already aired must NOT be reassigned.
 *
 * For each relation, remaining = max(0, promised - consumed). The pool of remaining
 * (plus any extraPool) is split equally across (relations.length + extraSlots).
 * Each relation's new promised_shows = consumed + share, so consumed never moves.
 *
 * @param {object}  params
 * @param {Array}   params.relations              Relations to redistribute over.
 * @param {object}  params.reportSingleData       Report payload used by calculateImpressions.
 * @param {number}  [params.displayId]            Override id used to look up the report row.
 *                                                Defaults to each rel.displayId.
 * @param {number}  [params.extraSlots=0]         Slots that receive a share but are not in
 *                                                `relations` (e.g. a brand-new relation about
 *                                                to be created with consumed = 0).
 * @param {number}  [params.extraPaidPool=0]      Paid shows added to the pool with no consumed
 *                                                (e.g. placeholders being absorbed).
 * @param {number}  [params.extraSubsidizedPool=0]
 * @param {boolean} [params.includeSubsidized=true]
 * @returns {{
 *   updates: Array<{ promised_shows: number, promised_shows_subsidized?: number }>,
 *   newSlotPaid: number,
 *   newSlotSubsidized: number
 * }} updates[i] is aligned with relations[i]. newSlot* is the share for the (single) extra slot.
 */
export function redistributePendingShows({
  relations,
  reportSingleData,
  displayId,
  extraSlots = 0,
  extraPaidPool = 0,
  extraSubsidizedPool = 0,
  includeSubsidized = true,
}) {
  const totalSlots = relations.length + extraSlots;
  if (totalSlots === 0) {
    return { updates: [], newSlotPaid: 0, newSlotSubsidized: 0 };
  }

  const remainings = relations.map(rel => {
    const id = displayId !== undefined ? displayId : rel.displayId;
    return {
      paid: Math.max(
        0,
        calculateImpressions('to_consumed_not_subsidized', rel, reportSingleData, { id })
      ),
      subsidized: includeSubsidized
        ? calculateImpressions('to_consumed_subsidized', rel, reportSingleData, { id }) || 0
        : 0,
    };
  });

  const totalPaid = remainings.reduce((s, r) => s + r.paid, 0) + extraPaidPool;
  const totalSubs = remainings.reduce((s, r) => s + r.subsidized, 0) + extraSubsidizedPool;

  const perPaid = Math.floor(totalPaid / totalSlots);
  let remPaid = totalPaid - perPaid * totalSlots;
  const perSubs = Math.floor(totalSubs / totalSlots);
  let remSubs = totalSubs - perSubs * totalSlots;

  const updates = relations.map((rel, i) => {
    const consumedPaid = (rel.promised_shows || 0) - remainings[i].paid;
    const extraP = remPaid > 0 ? 1 : 0;
    if (extraP) remPaid--;

    const update = { promised_shows: consumedPaid + perPaid + extraP };

    if (includeSubsidized) {
      const consumedSubs = (rel.promised_shows_subsidized || 0) - remainings[i].subsidized;
      const extraS = remSubs > 0 ? 1 : 0;
      if (extraS) remSubs--;
      update.promised_shows_subsidized = consumedSubs + perSubs + extraS;
    }
    return update;
  });

  let newSlotPaid = perPaid;
  if (remPaid > 0) {
    newSlotPaid += 1;
    remPaid--;
  }
  let newSlotSubsidized = includeSubsidized ? perSubs : 0;
  if (includeSubsidized && remSubs > 0) {
    newSlotSubsidized += 1;
    remSubs--;
  }

  return { updates, newSlotPaid, newSlotSubsidized };
}
